// Servicio para el panel admin /stays-webhooks.
// Lee el firehose de webhooks recibidos de Stays.

import { apiFetch } from "./apiClient";

export type WebhookOutcome =
  | "accepted_new"
  | "accepted_updated"
  | "ignored_older"
  | "ignored_other"
  | "rejected_signature"
  | "rejected_tenant"
  | "rejected_body"
  | "rejected_dt"
  | "rejected_stale"
  | "rejected_future"
  | "rejected_entity"
  | "error_persist";

export type WebhookActionType = "reservation" | "payment" | "other";

// Estado del evento en `stays_events` — la cola que el cron procesa
// DESPUÉS de aceptar el webhook. `null` cuando el POST nunca llegó a
// stays_events (ej. fue rechazado por firma o ignored_other).
export type ProcessingStatus = "queued" | "processing" | "done" | "failed";

// Resolución de la propiedad referenciada en el webhook (body.payload._idlisting).
// Distinguimos tres casos para que la UI muestre el mensaje correcto:
//   - found:true → la propiedad existe en AF (tiene FK válida)
//   - found:false → el listing vino en el body pero no existe en AF
//                    (es la causa del PROPERTY_NOT_FOUND)
//   - null → el webhook no traía _idlisting (other, rechazo temprano, etc.)
export type PropertyResolution =
  | {
      found: true;
      id: number;
      shortId: string | null;
      nombre: string;
      listingId: string;
    }
  | {
      found: false;
      listingId: string;
    };

// Estado del procesamiento posterior al webhook (cron stays.service).
export interface ProcessingResolution {
  status: ProcessingStatus;
  lastError: string | null;
  attempts: number;
}

export interface StaysWebhookLogRow {
  id: number;
  receivedAt: string;
  outcome: WebhookOutcome;
  httpStatus: number;
  action: string | null;
  actionType: WebhookActionType | null;
  entityKey: string | null;
  stayDt: string | null;
  headers: Record<string, string | null> | null;
  body: Record<string, unknown> | null;
  sourceIp: string | null;
  errorCode: string | null;
  createdAt: string;
  updatedAt: string;
  // Campos enriquecidos por el backend (round 2026-05-29). Pueden ser null
  // cuando el webhook no aplica (ver tipos arriba).
  property: PropertyResolution | null;
  processing: ProcessingResolution | null;
}

export interface ListWebhookLogsParams {
  page?: number;
  pageSize?: number;
  outcome?: WebhookOutcome | "";
  actionType?: WebhookActionType | "";
  entityKey?: string;
  search?: string;
  from?: string; // ISO
  to?: string;   // ISO
  /** "true" → sólo filas cuya propiedad referenciada no existe en AF. */
  propertyNotFound?: "true" | "";
}

export interface ListWebhookLogsResponse {
  rows: StaysWebhookLogRow[];
  total: number;
  page: number;
  pageSize: number;
}

export interface OutcomeCountsResponse {
  counts: Record<string, number>;
  total: number;
}

export interface ExportWebhookLogsResponse {
  rows: StaysWebhookLogRow[];
  total: number;
  truncated: boolean;
}

export interface ExportWebhookLogsParams extends ListWebhookLogsParams {
  /** Lista de IDs específicos a exportar. Si viene, los demás filtros se ignoran. */
  ids?: number[];
}

function buildQuery(params: Record<string, unknown> | ListWebhookLogsParams): string {
  const sp = new URLSearchParams();
  Object.entries(params).forEach(([k, v]) => {
    if (v === undefined || v === null || v === "") return;
    sp.append(k, String(v));
  });
  const s = sp.toString();
  return s ? `?${s}` : "";
}

export const staysWebhooksApi = {
  list: async (
    params: ListWebhookLogsParams = {},
  ): Promise<ListWebhookLogsResponse | null> => {
    const res = await apiFetch<{
      success: boolean;
      data: ListWebhookLogsResponse;
    }>(`/stays/webhook-log${buildQuery(params)}`);
    return res?.data ?? null;
  },

  outcomeCounts: async (): Promise<OutcomeCountsResponse | null> => {
    const res = await apiFetch<{
      success: boolean;
      data: OutcomeCountsResponse;
    }>(`/stays/webhook-log/outcomes`);
    return res?.data ?? null;
  },

  exportLogs: async (
    params: ExportWebhookLogsParams = {},
  ): Promise<ExportWebhookLogsResponse | null> => {
    const { ids, ...filters } = params;
    const queryParams: Record<string, unknown> = { ...filters };
    if (ids && ids.length > 0) queryParams.ids = ids.join(",");
    const res = await apiFetch<{
      success: boolean;
      data: ExportWebhookLogsResponse;
    }>(`/stays/webhook-log/export${buildQuery(queryParams)}`);
    return res?.data ?? null;
  },
};
